/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { createSelector, createSlice } from '@reduxjs/toolkit';

import { RootState } from '../../../store/rootReducer';
import { Product, ProductDetailsApiResponse, StockAvailabilityType, StocksState } from './types';
import { getPastProducts } from './products.slice';
import AuthSlice from '../../azure-b2c/storage';
import { TypesenseProduct } from '../../typesense/types';
import { storageDb } from '../../../services/localforage';
import _ from 'lodash';

export const stocksPersistConfig = {
  key: 'stocks',
  storage: storageDb,
  // serialize: false,
  blacklist: ['stocks'],
};

export const initialState: StocksState = {
  stocks: {},
};

export const StocksSlice = createSlice({
  name: 'stocks',
  initialState: initialState,
  extraReducers: (builder) => {
    builder.addCase(getPastProducts.fulfilled, (state, action) => {
      action.payload.products.forEach((product) => {
        if (!state.stocks[product.productId]) {
          state.stocks[product.productId] = product;
        } else {
          state.stocks[product.productId] = {
            ...state.stocks[product.productId],
            ...product,
          };
        }
      });
    });
    builder.addCase(AuthSlice.actions.setSelectedFarmerAccount, (state) => {
      state.stocks = {};
    });
  },
  reducers: {
    addSearchResults: (state, action) => {
      action.payload.map((document: TypesenseProduct) => {
        if (document.id) {
          const productId = document.id;
          const product = state.stocks[productId];
          state.stocks[productId] = { ...product, ...document, stockAvailability: document.stock };
        }
      });
    },
    addStock: (state, action) => {
      const productId = action.payload.productId || action.payload.masterProductId;
      const product = state.stocks[productId];
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { stockAvailabilities, ...newProduct } = action.payload;
      state.stocks[productId] = _.merge(product, newProduct);
    },
    addStockArray: (state, action) => {
      const stocksArray = action.payload as ProductDetailsApiResponse[];

      stocksArray.forEach((stock) => {
        const productId = stock.productId || stock.masterProductId;
        const product = state.stocks[productId];

        // Destructure and omit the stockAvailabilities field
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { stockAvailabilities, ...newProduct } = stock;

        // Merge the existing product data with the new data
        state.stocks[productId] = _.merge(product, newProduct);
      });
    },
    addAvailabilities: (state, action) => {
      const availabilities: StockAvailabilityType[] = action.payload || [];

      // Clear current product availabilities in store
      const productIds = [
        ...new Set(
          availabilities.map((availability: StockAvailabilityType) => availability.productId),
        ),
      ];
      productIds.forEach((productId) => {
        if (state.stocks[productId]) {
          state.stocks[productId].stockAvailabilities = [];
        }
      });

      availabilities.forEach((availability) => {
        if (state.stocks[availability.productId]) {
          if (!state.stocks[availability.productId].stockAvailabilities) {
            state.stocks[availability.productId].stockAvailabilities = [];
          }
          state.stocks[availability.productId].stockAvailabilities?.push(availability);
        } else {
          state.stocks[availability.productId] = {} as unknown as Product;
          state.stocks[availability.productId].stockAvailabilities?.push(availability);
        }
      });
    },
    clearStock: (state) => {
      state.stocks = {};
    },
  },
});

const selectState = (state: RootState) => state;

export const selectProducts = createSelector(selectState, (state) => {
  return state.products.products;
});

export const selectStockIds = createSelector(selectState, (state) => {
  return Object.keys(state.stocks.stocks);
});

export const selectProduct = (productId: number | undefined) => {
  return createSelector(selectState, (state): Product | undefined => {
    if (productId === undefined) return undefined;
    if (state.stocks.stocks[productId.toString()]) {
      return state.stocks.stocks[productId];
    }
  });
};

export const selectProductAvailabilities = (productId: number | undefined) => {
  return createSelector(selectState, (state): StockAvailabilityType[] | undefined => {
    if (productId === undefined) return undefined;
    if (state.stocks.stocks[productId.toString()]) {
      return state.stocks.stocks[productId].stockAvailabilities;
    }
  });
};
